/**
 * Enlaces entre documentos del vault.
 *
 * **Se escribe markdown estándar, no wikilinks.** Un `[[destino]]` no es
 * CommonMark: un `.md` escrito así se ve roto en GitHub, en VS Code y en
 * cualquier editor que no sea Obsidian, y el compromiso del proyecto es que los
 * `.md` son del usuario y portables (README §2, "sin sintaxis inventada"). Así
 * que Blanqo emite siempre `[Título](ruta/nota.md)`.
 *
 * Lo que sí se toma de Obsidian es la ERGONOMÍA: tipear `[[` abre el buscador
 * de archivos (`edit/linkMenu.ts`) y lo que inserta es un enlace markdown
 * común. Y un `[[destino]]` que ya exista —porque el vault viene de Obsidian—
 * se LEE y se navega igual; simplemente nunca se escribe uno nuevo.
 *
 * La resolución de un destino a una ruta del vault es puramente sintáctica y
 * se contesta contra el árbol que ya está en memoria (`makePathIndex`), no
 * contra el disco: un documento con cien enlaces no cuesta cien lecturas, y un
 * enlace roto se puede pintar en el mismo frame en que se renderiza.
 */

import { type VaultTreeNode } from "../model/types.ts";
import {
  decodePath,
  isExternalSrc,
  isHiddenFolderPath,
  pathCandidates,
} from "./attachments.ts";

/**
 * Extensiones que se prueban cuando el destino no trae ninguna, para que
 * `[algo](nota)` encuentre `nota.md`. El orden importa: un vault con `plan.md`
 * y `plan.canvas` resuelve al documento de texto, que es lo que se escribe al
 * autocompletar.
 */
const IMPLIED_EXT = [".md", ".canvas"];

/** Índice de rutas del vault en minúsculas → la ruta real, tal cual en disco. */
export type PathIndex = Map<string, string>;

/**
 * Índice de todos los archivos del árbol.
 *
 * La clave va en minúsculas porque macOS (APFS) y Windows lo son: un enlace a
 * `Notas/Idea.md` tiene que encontrar el archivo aunque en disco se llame
 * `notas/idea.md`. El valor guarda la capitalización real, que es la que se
 * abre y la que se vuelve a escribir.
 */
export function makePathIndex(tree: VaultTreeNode[]): PathIndex {
  const index: PathIndex = new Map();
  const walk = (nodes: VaultTreeNode[]): void => {
    for (const n of nodes) {
      if (n.children) walk(n.children);
      else index.set(n.path.toLowerCase(), n.path);
    }
  };
  walk(tree);
  return index;
}

/** Un destino que el navegador resuelve solo: no se busca en el vault. */
export function isExternalHref(href: string): boolean {
  // `//host/…` es una URL protocol-relative, y `#seccion` es un ancla dentro
  // del propio documento: ninguno de los dos apunta a otro archivo.
  return isExternalSrc(href) || href.startsWith("//") || href.startsWith("#");
}

/** Separa `nota.md#seccion` en su destino y su ancla (con `#`, o ""). */
export function splitHash(href: string): { target: string; hash: string } {
  const i = href.indexOf("#");
  if (i < 0) return { target: href, hash: "" };
  return { target: href.slice(0, i), hash: href.slice(i) };
}

/**
 * Una dirección web escrita como la escribe una persona, sin protocolo.
 *
 * Markdown interpreta `example.com` como una ruta relativa. En Blanqo eso la
 * hacía pasar por el resolver del vault y terminaba pintada como nota rota. Se
 * reconoce sólo una forma inequívocamente web: dominio, localhost o IPv4, con
 * puerto/ruta opcionales. Las rutas explícitas y los documentos que maneja la
 * app (`.md`/`.canvas`) siguen siendo internos.
 */
const BARE_WEB_RE =
  /^(?:www\.)?(?:(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}|localhost|(?:\d{1,3}\.){3}\d{1,3})(?::\d{1,5})?(?:[/?#].*)?$/i;

export function normalizeLinkDestination(value: string, targets: string[] = []): string {
  const raw = value.trim();
  if (raw === "" || isExternalHref(raw) || raw.startsWith("#")) return raw;

  const { target } = splitHash(raw);
  let decoded = target;
  try {
    decoded = decodeURIComponent(target);
  } catch {
    // Si el escape está incompleto se conserva tal cual; la app no debe
    // convertir silenciosamente una ruta interna dudosa en una URL externa.
  }
  if (targets.includes(decoded)) return raw;
  if (/^(?:\.\.?\/|\/)/.test(raw)) return raw;
  if (/\.(?:md|canvas)(?:[?#]|$)/i.test(raw)) return raw;

  return BARE_WEB_RE.test(raw) ? `https://${raw}` : raw;
}

/** La ruta real de `path` en el índice, probando las extensiones implícitas. */
function lookup(index: PathIndex, path: string): string | null {
  if (!path) return null;
  const direct = index.get(path.toLowerCase());
  if (direct) return direct;
  // Sólo se completa la extensión si no trae ninguna: `nota` puede ser
  // `nota.md`, pero `notas.viejas` no debería convertirse en `notas.viejas.md`.
  if (/\.[^./]+$/.test(path)) return null;
  for (const ext of IMPLIED_EXT) {
    const hit = index.get(`${path}${ext}`.toLowerCase());
    if (hit) return hit;
  }
  return null;
}

/**
 * La ruta del vault a la que apunta un enlace de `docPath`, o null si es
 * externo, si está vacío o si no existe tal archivo.
 *
 * Devolver null para un destino que no existe es lo que distingue un enlace
 * roto de uno bueno, y es deliberado que la misma función conteste las dos
 * preguntas: la vista lo usa para pintarlo en rojo y el barrido de renombrado
 * para NO tocar lo que no puede resolver con certeza.
 */
export function resolveHref(docPath: string, href: string, index: PathIndex): string | null {
  if (!href || isExternalHref(href)) return null;
  const { target } = splitHash(href);
  if (!target) return null;
  for (const candidate of pathCandidates(docPath, target)) {
    const hit = lookup(index, candidate);
    if (hit) return hit;
  }
  return null;
}

/**
 * La ruta del vault que un enlace roto QUERÍA nombrar, para poder ofrecer
 * crearla. Es la primera candidata (relativa a la carpeta del documento, como
 * Obsidian), con `.md` si no trae extensión.
 */
export function intendedPath(docPath: string, href: string): string | null {
  if (!href || isExternalHref(href)) return null;
  const { target } = splitHash(href);
  if (!target) return null;
  const path = pathCandidates(docPath, target)[0];
  if (!path) return null;
  return /\.[^./]+$/.test(path) ? path : `${path}.md`;
}

/**
 * Ruta de `to` relativa a la carpeta de `fromDoc`, que es la forma en que se
 * escriben los destinos: un `.md` movido junto con lo que enlaza sigue
 * andando, y se abre igual en cualquier otro editor.
 */
export function relativePath(fromDoc: string, to: string): string {
  const fromDirs = fromDoc.split("/").slice(0, -1);
  const toParts = to.split("/");
  const name = toParts.pop() ?? "";
  let shared = 0;
  while (shared < fromDirs.length && shared < toParts.length && fromDirs[shared] === toParts[shared]) {
    shared++;
  }
  const up = Array<string>(fromDirs.length - shared).fill("..");
  const down = toParts.slice(shared);
  return [...up, ...down, name].join("/");
}

/**
 * Escapa una ruta del vault para usarla como destino de un enlace markdown.
 * Sin esto, un nombre con espacios (`Mis notas.md`) no es siquiera un enlace.
 */
export function encodeTarget(path: string): string {
  return path.split("/").map(encodeURIComponent).join("/");
}

/** El destino que hay que escribir en `fromDoc` para apuntar a `to`. */
export function hrefFor(fromDoc: string, to: string, hash = ""): string {
  return `${encodeTarget(relativePath(fromDoc, to))}${hash}`;
}

/** El texto visible por defecto de un enlace a `path`: su nombre sin extensión. */
export function linkLabel(path: string): string {
  const base = path.split("/").pop() ?? path;
  return base.replace(/\.[^.]+$/, "");
}

/**
 * Un `[[destino]]` o `[[destino|texto]]` de un vault de Obsidian, convertido a
 * lo que Blanqo entiende. No se escribe nunca; se lee para que un vault
 * importado no llegue con la mitad de los enlaces muertos.
 */
export function parseWikiLink(inner: string): { target: string; label: string } {
  const pipe = inner.indexOf("|");
  const target = (pipe < 0 ? inner : inner.slice(0, pipe)).trim();
  const label = (pipe < 0 ? "" : inner.slice(pipe + 1).trim()) || linkLabel(decodePath(target));
  return { target, label };
}

/**
 * Los documentos del vault que un enlace puede nombrar, en orden de árbol.
 *
 * Se saltean las carpetas ocultas (`attachments/`): lo que no se ve en la
 * sidebar tampoco se ofrece al enlazar, o el buscador propondría destinos que
 * el usuario no puede encontrar después.
 */
export function linkTargets(tree: VaultTreeNode[]): string[] {
  const out: string[] = [];
  const walk = (nodes: VaultTreeNode[]): void => {
    for (const n of nodes) {
      if (n.children) {
        if (!isHiddenFolderPath(n.path)) walk(n.children);
      } else if (n.kind) {
        out.push(n.path);
      }
    }
  };
  walk(tree);
  return out;
}
